import { useEffect, useState } from 'react'
import { Nav } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router'
import LogoBox from '../common/logobox.jsx'
import { hrEsalary } from '../main'
import { authSelector } from '../reducers'
import {
	IconAbsences,
	IconAccountCash,
	IconAnniversaryCake,
	IconCalendar,
	IconCompetences,
	IconContracts,
	IconDiscussion,
	IconDocuments,
	IconEducation,
	IconEmployees,
	IconEmploymentCertificates,
	IconEmptyDoc,
	IconFactCheck,
	IconFullDoc,
	IconHoliday,
	IconMeters,
	IconMoney,
	IconNotifications,
	IconOrganization,
	IconPekkaset,
	IconPeople,
	IconPerson,
	IconPersonData,
	IconPersonnelStatement,
	IconPile,
	IconShoppingCart,
	IconStar,
	IconWorkHours,
	SimpleCalendarIcon,
} from './icons'
import { SidebarContainer } from './sidebar.styles.ts'
import { localized } from './utils'

const baseVersion = () => {
	const matches = import.meta.env.VITE_VERSION.match(/\d+\.\d+/)
	return matches === null ? null : matches[0]
}
const hrVersion = baseVersion()

export function Sidebar({ table, setTable, popular }) {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const auth = useSelector(authSelector)
	const [favorites, setFavorites] = useState(JSON.parse(localStorage.getItem('favSide')) ?? [])
	const [dragIndex, setDragIndex] = useState(null)
	const isBasicLevel = auth.hrLevel !== 'premium'
	const [isHover, setHover] = useState(false)
	const [isLocked, setLocked] = useState(
		localStorage.getItem('isMainMenuLocked') ? localStorage.getItem('isMainMenuLocked') === 'true' : true,
	)
	const expanded = isHover || isLocked

	const toggleLocked = () => {
		setLocked(!isLocked)
	}

	useEffect(() => {
		localStorage.setItem('isMainMenuLocked', isLocked)
	}, [isLocked])

	return (
		<SidebarContainer
			as="aside"
			className="nav col-auto px-0 d-none d-md-block"
			$expanded={expanded}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
		>
			<LogoBox toggleLocked={toggleLocked} isLocked={isLocked} expanded={expanded} />
			{!auth.personalProfile && popular && expanded && (
				<div className="px-3 pt-2 text-end small">
					<a href={`//files.esalary.fi/hr/Release-Notes-HR-${hrVersion}.pdf`} target="_blank" rel="noreferrer">
						{t('ReleaseNotes', 'Versiotiedote')} {hrVersion}
					</a>
				</div>
			)}
			<div className="nav-elements">
				<hr className="mt-n2" />
				{popular && expanded && <div className="popular mb-3">{t('Popular', 'Suosituimmat')}</div>}
				{table.map((row, idx) => {
					if (
						isBasicLevel &&
						(row.path === '/mgmt/competences' || row.path === '/mgmt/discussions' || row.path === '/mgmt/equipment')
					) {
						return null
					}

					const linkProps = {}
					if (isBasicLevel) {
						switch (row.path) {
							case '/mgmt/competences':
							case '/mgmt/discussions':
							case '/mgmt/equipment':
								linkProps.className = 'premium'
								linkProps.onClick = (e) => {
									e.stopPropagation() // disable menu item activation
									e.preventDefault() // disable click navigation
									dispatch(localized.premiumAlert)
								}
						}
					}

					const isFavorite = favorites.some((item) => item.path === row.path) // Check if the row is in favorites

					if (popular) {
						linkProps.draggable = true
						//linkProps.onDragEnter = () => setHoverIndex(idx); // TODO: highlight the hovered item
						//linkProps.onDragLeave = () => setHoverIndex(null);
						//linkProps.onDragEnd = () => setHoverIndex(null);
						linkProps.onDragStart = () => {
							setDragIndex(idx)
							//e.target.style.opacity = "0.5"; // Optional: make the dragged item semi-transparent
						}
						linkProps.onDragOver = (e) => e.preventDefault()
						linkProps.onDrop = () => {
							if (dragIndex === null || dragIndex === idx) return
							// Reorder favorites based on drag and drop
							const updated = [...favorites]
							const draggedItem = updated[dragIndex]
							updated.splice(dragIndex, 1)
							updated.splice(idx, 0, draggedItem)
							localStorage.setItem('favSide', JSON.stringify(updated))
							//setFavorites(updated);
							setTable(updated)
							setDragIndex(null)
							//setHoverIndex(null);
						}
					}

					return (
						<Nav.Link
							active={location.pathname.startsWith(row.path)}
							as={Link}
							to={linkProps.className ? '/premium' : row.path}
							key={row.title}
							{...linkProps}
						>
							<div>
								{row.icon === 'IconEmployees' && <IconEmployees />}
								{row.icon === 'IconPeople' && <IconPeople />}
								{row.icon === 'IconCompetences' && <IconCompetences />}
								{row.icon === 'IconContracts' && <IconContracts />}
								{row.icon === 'IconEmploymentCertificates' && <IconEmploymentCertificates />}
								{row.icon === 'IconDiscussion' && <IconDiscussion />}
								{row.icon === 'IconOrganization' && <IconOrganization />}
								{row.icon === 'IconFactCheck' && <IconFactCheck />}
								{row.icon === 'IconDocuments' && <IconDocuments />}
								{row.icon === 'IconMeters' && <IconMeters />}
								{row.icon === 'IconEducation' && <IconEducation />}
								{row.icon === 'IconNotifications' && <IconNotifications />}
								{row.icon === 'IconShoppingCart' && <IconShoppingCart />}
								{row.icon === 'IconEmptyDoc' && <IconEmptyDoc />}
								{row.icon === 'IconFullDoc' && <IconFullDoc />}
								{row.icon === 'IconPile' && <IconPile />}
								{row.icon === 'IconAnniversaryCake' && <IconAnniversaryCake />}
								{row.icon === 'IconHoliday' && <IconHoliday />}
								{row.icon === 'IconWorkHours' && <IconWorkHours />}
								{row.icon === 'IconPerson' && <IconPerson />}
								{row.icon === 'IconPersonData' && <IconPersonData />}
								{row.icon === 'IconAbsences' && <IconAbsences />}
								{row.icon === 'IconPekkaset' && <IconPekkaset />}
								{row.icon === 'IconPersonnelStatement' && <IconPersonnelStatement />}
								{row.icon === 'IconAccountCash' && <IconAccountCash />}
								{row.icon === 'IconMoney' && <IconMoney />}
								{row.icon === 'IconCalendar' && <IconCalendar />}
								{row.icon === 'IconSimpleCalendar' && <SimpleCalendarIcon />}
								<span className="title">{row.title}</span>
							</div>
							{
								/* !(location.pathname === "/" || location.pathname.startsWith("/personal")) && */
								<button
									type="button"
									className={`star-button ${isFavorite ? '' : 'star'}`}
									onClick={(e) => {
										e.preventDefault()
										let newFavorites

										if (isFavorite) {
											newFavorites = favorites.filter((x) => x.path !== row.path) // remove from favorites
										} else {
											newFavorites = [...favorites, row]
										}
										localStorage.setItem('favSide', JSON.stringify(newFavorites))
										setFavorites(newFavorites)
									}}
								>
									<IconStar fill={isFavorite} />
								</button>
							}
						</Nav.Link>
					)
				})}
			</div>
		</SidebarContainer>
	)
}

export function DashboardSidebar() {
	// Favorites
	const { t } = useTranslation()
	const auth = useSelector(authSelector)

	const sidebar_table = []
	const favorites = JSON.parse(localStorage.getItem('favSide')) ?? []

	// TODO: Per-role favorites
	if (favorites.length > 0) {
		sidebar_table.push(...favorites)
	} else if (auth.role === 'EQUIPMENT') {
		sidebar_table.push({
			path: '/mgmt/equipment',
			title: t('EquipmentAndAccessRights', 'Välineistö ja käyttöoikeudet'),
			icon: 'IconShoppingCart',
		})
	} else if (auth.personalProfile) {
		sidebar_table.push({ path: '/personal/profile', title: t('MyProfile', 'Oma profiili'), icon: 'IconPerson' })
	} else {
		if (auth.persons) {
			sidebar_table.push({ path: '/mgmt/persons', title: t('Persons', 'Henkilöt'), icon: 'IconPeople' })
		}
		if (auth.knowledge) {
			sidebar_table.push({ path: '/mgmt/competences', title: t('Competences', 'Osaaminen'), icon: 'IconCompetences' })
		}
		if (auth.statistics) {
			sidebar_table.push({
				path: '/stats/vacancy',
				title: t('AnnualVacationStatistics', 'Vuosilomatilastot'),
				icon: 'IconHoliday',
			})
		}
	}

	const [table, setTable] = useState(sidebar_table)

	return <Sidebar table={table} setTable={setTable} popular />
}

export function MgmtSidebar() {
	const { t } = useTranslation()
	const auth = useSelector(authSelector)
	const { equipmentManager } = auth.role === 'EQUIPMENT'

	const sidebar_table = []

	if (!equipmentManager && auth.persons) {
		sidebar_table.push({ path: '/mgmt/persons', title: t('Persons', 'Henkilöt'), icon: 'IconPeople' })
		sidebar_table.push({ path: '/mgmt/calendar', title: t('Events', 'Tapahtumat'), icon: 'IconCalendar' })
	}

	if (!equipmentManager && auth.knowledge) {
		sidebar_table.push({ path: '/mgmt/competences', title: t('Competences', 'Osaaminen'), icon: 'IconCompetences' })
	}

	if (!equipmentManager && auth.contracts) {
		sidebar_table.push({ path: '/mgmt/contracts', title: t('Contracts', 'Työsopimukset'), icon: 'IconContracts' })
	}

	if (!equipmentManager && auth.discussions) {
		sidebar_table.push({ path: '/mgmt/discussions', title: t('Discussions', 'Keskustelut'), icon: 'IconDiscussion' })
	}

	if (auth.equipment) {
		sidebar_table.push({
			path: '/mgmt/equipment',
			title: t('EquipmentAndAccessRights', 'Välineistö ja käyttöoikeudet'),
			icon: 'IconShoppingCart',
		})
	}

	if (!equipmentManager && auth.contracts) {
		sidebar_table.push({
			path: '/mgmt/employmentcertificates',
			title: t('EmploymentCertificates', 'Työtodistukset'),
			icon: 'IconEmploymentCertificates',
		})
	}
	//    { path: "/mgmt/m4", title: t("ConversationTemplates", "Keskustelupohjat"), icon: null },
	//    { path: "/mgmt/m6", title: t("Recruitment", "Rekrytointi"), icon: null }

	return <Sidebar table={sidebar_table} />
}

export function StatisticsSidebar() {
	const { t } = useTranslation()
	const auth = useSelector(authSelector)

	const sidebar_table = [
		{ path: '/stats/anniversaries', title: t('Anniversaries', 'Merkkipäivät'), icon: 'IconAnniversaryCake' },
		{ path: '/stats/vacancy', title: t('AnnualVacationStatistics', 'Vuosilomatilastot'), icon: 'IconHoliday' },
		{ path: '/stats/workhours', title: t('WorkinghoursAndOverTime', 'Työtunnit ja ylityöt'), icon: 'IconWorkHours' },
		{ path: '/stats/salaryabsences', title: t('Absences', 'Poissaolot'), icon: 'IconAbsences' },
	]

	if (auth.company?.pekkaset) {
		sidebar_table.push({ path: '/stats/pekkaset', title: t('Pekkaset', 'Pekkaset'), icon: 'IconPekkaset' })
	}

	sidebar_table.push({
		path: '/stats/employmenthistory',
		title: t('PersonnelAndEmploymentInfo', 'Henkilöstö ja työsuhdetiedot'),
		icon: 'IconPersonData',
	})

	if (auth.role === 'ADMIN' || auth.role === 'SYSTEM') {
		sidebar_table.push({
			path: '/stats/statements',
			title: t('PersonnelStatements', 'Henkilöstökertomus'),
			icon: 'IconPersonnelStatement',
		})
	}
	if (auth.salary) {
		sidebar_table.push({
			path: '/stats/salaryhistory',
			title: t('SalaryHistory', 'Palkkahistoria'),
			icon: 'IconAccountCash',
		})
	}
	return <Sidebar table={sidebar_table} />
}

export function OrganizationSidebar() {
	const { t } = useTranslation()

	const auth = useSelector(authSelector)
	const isBasicLevel = auth.hrLevel !== 'premium'
	const sidebar_table = []

	if (auth.admin) {
		// admin = ePalkat settings permission
		sidebar_table.push({
			path: '/organization/details',
			title: t('OrganizationDetails', 'Organisaation tiedot'),
			icon: 'IconFactCheck',
		})
	}
	sidebar_table.push({ path: '/organization/documents', title: t('Documents', 'Dokumentit'), icon: 'IconDocuments' })
	if (!auth.personalProfile && !isBasicLevel && auth.admin) {
		sidebar_table.push({ path: '/organization/integrations', title: t('Integrations', 'Liittymät'), icon: 'IconPile' })
	}
	sidebar_table.push(
		//{ path: "/organization/absences", title: t("Absences", "Poissaolot"), icon: "IconDocuments" },
		{ path: '/organization/hierarchy', title: t('Hierarchy', 'Hierarkia'), icon: 'IconPile' },
	)

	return <Sidebar table={sidebar_table} />
}

export function SettingsSidebar() {
	const { t } = useTranslation()

	const sidebar_table = [
		{ path: '/settings/users', title: t('UserManagement', 'Käyttäjähallinta'), icon: 'IconPeople' },
	]

	if (location.hostname !== hrEsalary) {
		sidebar_table.push({ path: '/settings/calendar', title: t('CalendarSettings'), icon: 'IconSimpleCalendar' })
	}

	return <Sidebar table={sidebar_table} />
}

export function NotifSidebar() {
	const { t } = useTranslation()

	const sidebar_table = [
		{ path: '/notif/n1', title: t('Notif-1', 'Notif-1'), icon: 'IconShoppingCart' },
		{ path: '/notif/n2', title: t('Notifications', 'Ilmoitukset'), icon: 'IconNotifications' },
		{ path: '/notif/n3', title: t('Notif-3', 'Notif-3'), icon: 'IconPile' },
		{ path: '/notif/n4', title: t('Notif-4', 'Notif-4'), icon: 'IconEmptyDoc' },
		{ path: '/notif/n5', title: t('Notif-5', 'Notif-5'), icon: 'IconFullDoc' },
		{ path: '/notif/n6', title: t('Notif-6', 'Notif-6'), icon: 'IconEmployees' },
		{ path: '/notif/n7', title: t('Notif-7', 'Notif-7'), icon: 'IconEmptyDoc' },
	]

	return <Sidebar table={sidebar_table} />
}

export function SalarySidebar() {
	const { t } = useTranslation()
	const { presalary, paymentsApproval, company } = useSelector(authSelector)
	const sidebar_table = []

	if (presalary) {
		sidebar_table.push({
			path: '/epalkat/one-time-payment',
			title: t('OneOffRemuneration', 'Kertakorvaus'),
			icon: 'IconAccountCash',
		})
	}

	if (location.hostname !== hrEsalary && presalary && company?.salarySheet) {
		sidebar_table.push({
			path: '/epalkat/salarytemplate',
			title: t('SalaryTemplate', 'Palkkapohja'),
			icon: 'IconDocuments',
		})
	}

	if (paymentsApproval) {
		sidebar_table.push({
			path: '/epalkat/paymentapproval',
			title: t('PaymentsApproval', 'Maksujen hyväksyntä'),
			icon: 'IconMoney',
		})
	}

	if (location.hostname !== hrEsalary && presalary) {
		sidebar_table.push(
			{ path: '/epalkat/absences', title: t('Absences', 'Poissaolot'), icon: 'IconDocuments' },
			{
				path: '/epalkat/workhours',
				title: t('WorkinghoursAndOverTime', 'Työtunnit ja ylityöt'),
				icon: 'IconWorkHours',
			},
		)
	}

	return <Sidebar table={sidebar_table} />
}

export function PersonalSidebar() {
	const { t } = useTranslation()

	const sidebar_table = [
		{
			path: '/personal/dashboard',
			title: t('MyProfile', 'Oma profiili'),
			icon: 'IconPerson',
		},
		{
			path: '/personal/calendar',
			title: t('Events', 'Tapahtumat'),
			icon: 'IconCalendar',
		},
		{
			path: '/personal/competences',
			title: t('Competences', 'Osaaminen'),
			icon: 'IconCompetences',
		},
		{
			path: '/personal/contracts',
			title: t('Contracts', 'Työsopimukset'),
			icon: 'IconContracts',
		},
		{
			path: '/personal/discussions',
			title: t('Discussions', 'Keskustelut'),
			icon: 'IconDiscussion',
		},
		{
			path: '/personal/equipment',
			title: t('EquipmentAndAccessRights', 'Välineistö ja käyttöoikeudet'),
			icon: 'IconShoppingCart',
		},
		{
			path: '/personal/employmentcertificates',
			title: t('EmploymentCertificates', 'Työtodistukset'),
			icon: 'IconEmploymentCertificates',
		},
	]

	return <Sidebar table={sidebar_table} />
}
